import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus, Pencil, Trash2, Upload, X, Loader2, Image as ImageIcon,
  ArrowUp, ArrowDown, Images, Package, ExternalLink
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

/* Shared image picker: uploads to /api/upload/image and hands back an
   absolute URL, or accepts a pasted URL. */
function ImagePicker({ value, onChange, height = 'h-48' }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowed = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowed.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, GIF or WEBP');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Maximum size is 5MB');
      return;
    }

    setUploading(true);
    try {
      const data = new FormData();
      data.append('file', file);
      const res = await axios.post(`${API}/upload/image`, data, {
        headers: { ...authHeaders().headers, 'Content-Type': 'multipart/form-data' }
      });
      onChange(`${BACKEND_URL}${res.data.image_url}`);
      toast.success('Image uploaded');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-2">
      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
        {value ? (
          <div className="relative">
            <img src={value} alt="Preview" className={`w-full ${height} object-cover rounded-lg`} />
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              onClick={() => onChange('')}
            >
              <X size={16} />
            </Button>
          </div>
        ) : (
          <div
            className={`flex flex-col items-center justify-center ${height} cursor-pointer hover:bg-gray-50 rounded-lg transition-colors`}
            onClick={() => inputRef.current?.click()}
          >
            {uploading ? (
              <Loader2 className="h-12 w-12 text-gray-400 animate-spin" />
            ) : (
              <>
                <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
                <p className="text-sm text-gray-600 text-center">
                  Click to upload an image<br />
                  <span className="text-xs text-gray-400">JPEG, PNG, GIF, WEBP (max 5MB)</span>
                </p>
              </>
            )}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFile}
          className="hidden"
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="button" variant="outline" size="sm" disabled={uploading} onClick={() => inputRef.current?.click()}>
          <Upload size={16} className="mr-2" />
          {uploading ? 'Uploading...' : 'Upload Image'}
        </Button>
        <span className="text-xs text-gray-500">or paste an image URL below</span>
      </div>
      <Input
        value={value || ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder="https://example.com/image.jpg"
      />
    </div>
  );
}

const EMPTY_SLIDE = {
  image_url: '',
  title: '',
  subtitle: '',
  cta_text: '',
  cta_link: '',
  sort_order: 0,
  is_active: true
};

function SliderTab() {
  const [slides, setSlides] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(EMPTY_SLIDE);

  const fetchSlides = async () => {
    try {
      const res = await axios.get(`${API}/homepage/slides`, authHeaders());
      setSlides(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load slides');
    }
  };

  useEffect(() => { fetchSlides(); }, []);

  const openNew = () => {
    setEditing(null);
    setForm({ ...EMPTY_SLIDE, sort_order: slides.length });
    setOpen(true);
  };

  const openEdit = (slide) => {
    setEditing(slide);
    setForm({
      image_url: slide.image_url || '',
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      cta_text: slide.cta_text || '',
      cta_link: slide.cta_link || '',
      sort_order: slide.sort_order || 0,
      is_active: slide.is_active !== false
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.image_url) {
      toast.error('Please upload or paste an image for the slide');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await axios.put(`${API}/homepage/slides/${editing.id}`, form, authHeaders());
        toast.success('Slide updated');
      } else {
        await axios.post(`${API}/homepage/slides`, form, authHeaders());
        toast.success('Slide added');
      }
      setOpen(false);
      fetchSlides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save slide');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (slide) => {
    if (!window.confirm('Delete this slide from the homepage?')) return;
    try {
      await axios.delete(`${API}/homepage/slides/${slide.id}`, authHeaders());
      toast.success('Slide deleted');
      fetchSlides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete slide');
    }
  };

  const patch = async (slide, changes) => {
    const body = {
      image_url: slide.image_url,
      title: slide.title || '',
      subtitle: slide.subtitle || '',
      cta_text: slide.cta_text || '',
      cta_link: slide.cta_link || '',
      sort_order: slide.sort_order || 0,
      is_active: slide.is_active !== false,
      ...changes
    };
    try {
      await axios.put(`${API}/homepage/slides/${slide.id}`, body, authHeaders());
      fetchSlides();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update slide');
    }
  };

  // Swapping sort_order with the neighbour keeps the ordering stable even when
  // two slides were saved with the same number.
  const move = async (index, direction) => {
    const neighbour = slides[index + direction];
    const slide = slides[index];
    if (!neighbour) return;
    await patch(slide, { sort_order: index + direction });
    await patch(neighbour, { sort_order: index });
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-slate-600">
          These images rotate in the hero banner at the top of the public welcome page.
          Slides are shown in the order below; inactive slides are hidden from visitors.
        </p>
        <Button onClick={openNew} data-testid="add-slide-button">
          <Plus size={16} className="mr-2" /> Add Slide
        </Button>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-24">Order</TableHead>
              <TableHead>Image</TableHead>
              <TableHead>Heading</TableHead>
              <TableHead>Button</TableHead>
              <TableHead>Visible</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {slides.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                  <Images size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No slides yet</p>
                  <p className="text-sm">Add slides to replace the default homepage banner image.</p>
                </TableCell>
              </TableRow>
            ) : (
              slides.map((slide, index) => (
                <TableRow key={slide.id} data-testid={`slide-row-${slide.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" disabled={index === 0} onClick={() => move(index, -1)}>
                        <ArrowUp size={14} />
                      </Button>
                      <Button variant="ghost" size="icon" disabled={index === slides.length - 1} onClick={() => move(index, 1)}>
                        <ArrowDown size={14} />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <img src={slide.image_url} alt={slide.title || 'Slide'} className="w-28 h-16 object-cover rounded border" />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium">{slide.title || <span className="text-gray-400">— no heading —</span>}</p>
                    <p className="text-sm text-gray-600 truncate max-w-[260px]">{slide.subtitle}</p>
                  </TableCell>
                  <TableCell className="text-sm">
                    {slide.cta_text ? (
                      <span className="inline-flex items-center gap-1">
                        {slide.cta_text}
                        <ExternalLink size={12} className="text-gray-400" />
                      </span>
                    ) : <span className="text-gray-400">—</span>}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={slide.is_active !== false}
                      onCheckedChange={(checked) => patch(slide, { is_active: checked })}
                      data-testid={`slide-active-${slide.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(slide)} data-testid={`edit-slide-${slide.id}`}>
                      <Pencil size={16} />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => remove(slide)} data-testid={`delete-slide-${slide.id}`}>
                      <Trash2 size={16} className="text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="slide-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Slide' : 'Add Slide'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>Slide Image *</Label>
              <ImagePicker value={form.image_url} onChange={(url) => setForm({ ...form, image_url: url })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slide-title">Heading</Label>
              <Input
                id="slide-title"
                data-testid="slide-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="e.g., Premium Quality Chalk for Education"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slide-subtitle">Sub-heading</Label>
              <Textarea
                id="slide-subtitle"
                value={form.subtitle}
                onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                placeholder="Short supporting sentence shown under the heading"
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="slide-cta">Button Text</Label>
                <Input
                  id="slide-cta"
                  value={form.cta_text}
                  onChange={(e) => setForm({ ...form, cta_text: e.target.value })}
                  placeholder="e.g., Start Ordering"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="slide-link">Button Link</Label>
                <Input
                  id="slide-link"
                  value={form.cta_link}
                  onChange={(e) => setForm({ ...form, cta_link: e.target.value })}
                  placeholder="/customer/register"
                />
              </div>
            </div>
            <p className="text-xs text-gray-500 -mt-2">
              Leave the button empty to keep the default "Start Ordering" and "Customer Login" buttons.
            </p>
            <div className="flex items-center space-x-2">
              <Switch
                id="slide-visible"
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: checked })}
              />
              <Label htmlFor="slide-visible">Visible on the website</Label>
            </div>
            <Button type="submit" className="w-full" disabled={saving} data-testid="save-slide-button">
              {saving ? 'Saving...' : editing ? 'Update Slide' : 'Add Slide'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function FeaturedProductsTab() {
  const [products, setProducts] = useState([]);
  const [imageProduct, setImageProduct] = useState(null);
  const [imageUrl, setImageUrl] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchProducts = async () => {
    try {
      const res = await axios.get(`${API}/products`, authHeaders());
      setProducts(res.data);
    } catch (error) {
      toast.error('Failed to load products');
    }
  };

  useEffect(() => { fetchProducts(); }, []);

  // Presentation-only patch — prices and stock are never touched from here.
  const patchProduct = async (product, changes) => {
    try {
      await axios.put(`${API}/products/${product.id}/homepage`, changes, authHeaders());
      fetchProducts();
      return true;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update product');
      return false;
    }
  };

  const saveImage = async () => {
    setSaving(true);
    const ok = await patchProduct(imageProduct, { image_url: imageUrl });
    setSaving(false);
    if (ok) {
      toast.success('Product image updated');
      setImageProduct(null);
    }
  };

  const featuredCount = products.filter((p) => p.featured && p.is_active).length;

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-sm text-blue-900">
        <p className="font-semibold mb-1">How the homepage picks products</p>
        <p>
          The "Our Products" section on the welcome page shows only products that are
          <strong> Active</strong> and switched on below — up to six of them, in the order you set.
          Switch one off to hide it from the welcome page while keeping it on sale in the customer
          catalogue. With nothing switched on, the whole section disappears from the page.
          Currently showing: <strong>{featuredCount}</strong>.
        </p>
      </div>

      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product</TableHead>
              <TableHead>Retail</TableHead>
              <TableHead>Active</TableHead>
              <TableHead>Show on homepage</TableHead>
              <TableHead className="w-28">Order</TableHead>
              <TableHead className="text-right">Image</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {products.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-slate-500 py-12">
                  <Package size={48} className="mx-auto text-gray-400 mb-4" />
                  <p className="text-lg font-semibold mb-2">No products yet</p>
                  <p className="text-sm">Add products in the Products Catalog first.</p>
                </TableCell>
              </TableRow>
            ) : (
              products.map((product) => (
                <TableRow key={product.id} data-testid={`website-product-${product.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {product.image_url ? (
                        <img src={product.image_url} alt={product.name} className="w-14 h-14 object-cover rounded border" />
                      ) : (
                        <div className="w-14 h-14 bg-gray-100 rounded flex items-center justify-center border border-dashed">
                          <ImageIcon size={18} className="text-gray-400" />
                        </div>
                      )}
                      <div>
                        <p className="font-medium">{product.name}</p>
                        <p className="text-sm text-gray-600 truncate max-w-[240px]">{product.description}</p>
                        {!product.image_url && (
                          <p className="text-xs text-orange-600">No image — a placeholder is shown on the website</p>
                        )}
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium text-green-700">
                    Tsh {(product.retail_price || 0).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <Badge className={product.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'} variant="outline">
                      {product.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={!!product.featured}
                      disabled={!product.is_active}
                      onCheckedChange={(checked) => patchProduct(product, { featured: checked })}
                      data-testid={`featured-${product.id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="number"
                      className="w-20"
                      defaultValue={product.sort_order || 0}
                      onBlur={(e) => {
                        const next = parseInt(e.target.value, 10) || 0;
                        if (next !== (product.sort_order || 0)) patchProduct(product, { sort_order: next });
                      }}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => { setImageProduct(product); setImageUrl(product.image_url || ''); }}
                      data-testid={`product-image-${product.id}`}
                    >
                      <Upload size={14} className="mr-2" />
                      {product.image_url ? 'Change' : 'Upload'}
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!imageProduct} onOpenChange={(o) => { if (!o) setImageProduct(null); }}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Product Image — {imageProduct?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <ImagePicker value={imageUrl} onChange={setImageUrl} />
            <Button className="w-full" onClick={saveImage} disabled={saving} data-testid="save-product-image">
              {saving ? 'Saving...' : 'Save Image'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Website() {
  return (
    <div data-testid="website-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900">Website</h1>
        <p className="text-slate-600 mt-1">Control what visitors see on the public welcome page</p>
      </div>

      <Tabs defaultValue="slider">
        <TabsList className="mb-4">
          <TabsTrigger value="slider" data-testid="tab-slider">
            <Images size={16} className="mr-2" /> Homepage Slider
          </TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">
            <Package size={16} className="mr-2" /> Homepage Products
          </TabsTrigger>
        </TabsList>
        <TabsContent value="slider"><SliderTab /></TabsContent>
        <TabsContent value="products"><FeaturedProductsTab /></TabsContent>
      </Tabs>
    </div>
  );
}
